import { Link } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import {
  FiArrowUpRight,
  FiCheckCircle,
  FiCompass,
  FiMessageSquare,
  FiPlus,
  FiStar,
  FiX,
  FiCalendar,
  FiUsers,
  FiGlobe,
  FiSettings,
} from "react-icons/fi";

import "../styles/Hospitality.css";

const images = {
  hero:
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1800&q=85",
  booking:
    "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1000&q=85",
  service:
    "https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=1000&q=85",
  loyalty:
    "https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=1000&q=85",
  property:
    "https://images.unsplash.com/photo-1601918774946-25832a4be0d6?auto=format&fit=crop&w=1200&q=85",
  results:
    "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1400&q=85",
  closing:
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1800&q=85",
};

const cards = [
  [
    <FiCompass />,
    "Discovery & booking",
    "Inspiring, high-conversion journeys that turn interest into direct bookings.",
    images.booking,
  ],
  [
    <FiMessageSquare />,
    "Connected guest service",
    "Timely, personal communication before, during, and after every stay.",
    images.service,
  ],
  [
    <FiStar />,
    "Loyalty experiences",
    "Digital touchpoints that recognise guests and encourage their next visit.",
    images.loyalty,
  ],
];

const services = [
  [
    "Booking platforms",
    "Fast, brand-led booking journeys designed to convert.",
    <FiCalendar />,
  ],
  [
    "Guest portals",
    "One place for preferences, requests, itineraries, and support.",
    <FiUsers />,
  ],
  [
    "Property websites",
    "Beautiful, accessible websites that make your offering memorable.",
    <FiGlobe />,
  ],
  [
    "Operations automation",
    "Reduce manual handoffs and keep service teams in sync.",
    <FiSettings />,
  ],
];

const faqs = [
  [
    "What hospitality technology solutions do you provide?",
    "We create hospitality websites, booking journeys, guest portals, CRM workflows, mobile experiences, integrations, dashboards, and custom operational tools.",
  ],
  [
    "Can you help us increase direct bookings?",
    "Yes. We improve the hotel website and booking journey to make it faster, clearer, and more compelling for guests to book directly.",
  ],
  [
    "Can guests manage their stay online?",
    "Yes. Guest portals can let visitors manage reservations, preferences, requests, itineraries, and support.",
  ],
  [
    "Can you create loyalty-program experiences?",
    "Yes. We can build digital touchpoints that recognise returning guests, communicate relevant offers, and encourage repeat stays.",
  ],
  [
    "Can you improve communication before and during a stay?",
    "Yes. We can create automated yet personalized messages for booking confirmation, arrival, stay updates, and post-stay follow-up.",
  ],
  [
    "Can you build a custom hotel booking platform?",
    "Yes. We can design direct booking experiences around your inventory, brand, integrations, payment requirements, and guest needs.",
  ],
  [
    "Can you connect our existing hotel systems and APIs?",
    "Yes. We plan integrations carefully so booking, CRM, property, payment, and marketing systems work together more smoothly.",
  ],
];

function Reveal({ children, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;

    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          element.classList.add("is-visible");
          observer.unobserve(element);
        }
      },
      { threshold: 0.12 }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`hospitality-reveal ${className}`}>
      {children}
    </div>
  );
}

export default function Hospitality() {
  const [open, setOpen] = useState(0);

  return (
    <main className="hospitality-page sector-page">

      {/* HERO */}
      <section
        className="hospitality-hero"
        style={{ "--hero-image": `url("${images.hero}")` }}
      >
        <div className="hospitality-hero-overlay" />

        <div className="hospitality-hero-grid">
          <Reveal className="hospitality-hero-content">
            <span className="hospitality-eyebrow">
              Techlynx for Hospitality
            </span>

            <h1>
              Guest experiences worth{" "}
              <em>returning to</em>
            </h1>

            <p>
              We help hospitality brands connect every stage of the guest
              journey—from discovery and booking to loyalty, service, and
              return visits.
            </p>

            <a href="#hospitality-solutions" className="hospitality-primary-btn">
              Explore solutions
              <FiArrowUpRight />
            </a>
          </Reveal>

          <Reveal className="hospitality-hero-visual">
            <div className="hero-orbit orbit-one" />
            <div className="hero-orbit orbit-two" />

            <div className="hero-star">
              <FiStar />
            </div>
          </Reveal>
        </div>

        <div className="hero-scroll-line">
          <span />
          SCROLL TO EXPLORE
        </div>
      </section>

      {/* CAPABILITIES */}
      <section
        id="hospitality-solutions"
        className="hospitality-section hospitality-capabilities"
      >
        <div className="hospitality-section-grid">
          <Reveal className="hospitality-section-intro">
            <span className="hospitality-eyebrow">
              Hospitality technology
            </span>

            <h2>
              Every stay starts with a{" "}
              <em>great experience</em>
            </h2>

            <p>
              We pair thoughtful brand moments with dependable operational
              tools, so teams can make every guest interaction count.
            </p>

            <div className="section-number">01</div>
          </Reveal>

          <div className="hospitality-capability-grid">
            {cards.map(([icon, title, text, image], index) => (
              <Reveal
                key={title}
                className={`capability-reveal delay-${index + 1}`}
              >
                <article className="hospitality-capability-card">
                  <div className="capability-image">
                    <img src={image} alt={title} loading="lazy" />
                    <div className="image-gradient" />
                  </div>

                  <div className="capability-content">
                    <div className="capability-icon">{icon}</div>

                    <div className="capability-index">
                      0{index + 1}
                    </div>

                    <h3>{title}</h3>

                    <p>{text}</p>

                    <div className="capability-line" />
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="hospitality-section hospitality-services">
        <div className="hospitality-services-heading">
          <Reveal>
            <span className="hospitality-eyebrow">
              Where we add value
            </span>

            <h2>
              Designed around every{" "}
              <em>guest moment</em>
            </h2>
          </Reveal>
        </div>

        <div className="hospitality-service-layout">
          <Reveal className="hospitality-property-visual">
            <div className="property-image-wrap">
              <img
                src={images.property}
                alt="Premium hospitality property"
                loading="lazy"
              />

              <div className="property-glow" />

              <div className="property-caption">
                <span>01 / EXPERIENCE</span>
                <strong>Beautiful by design.</strong>
              </div>
            </div>
          </Reveal>

          <div className="hospitality-service-grid">
            {services.map(([title, text, icon], index) => (
              <Reveal key={title} className={`delay-${index + 1}`}>
                <article className="hospitality-service-card">
                  <div className="service-card-top">
                    <b>0{index + 1}</b>
                    <div>{icon}</div>
                  </div>

                  <h3>{title}</h3>

                  <p>{text}</p>

                  <span className="service-arrow">
                    <FiArrowUpRight />
                  </span>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="hospitality-section hospitality-results">
        <div className="results-background">
          <img
            src={images.results}
            alt=""
            loading="lazy"
          />
          <div />
        </div>

        <div className="results-content">
          <Reveal>
            <span className="hospitality-eyebrow">
              Built for better stays
            </span>

            <h2>
              Technology that creates{" "}
              <em>lasting loyalty</em>
            </h2>
          </Reveal>

          <div className="result-points">
            {[
              "More direct, high-intent bookings",
              "Personalised guest communication",
              "Connected property operations",
              "Loyalty that grows with every stay",
            ].map((text, index) => (
              <Reveal key={text} className={`delay-${index + 1}`}>
                <p>
                  <span className="result-icon">
                    <FiCheckCircle />
                  </span>
                  {text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="results-floating-label">
          <FiStar />
          <span>GUEST FIRST</span>
        </div>
      </section>

      {/* FAQ */}
      <section className="hospitality-section hospitality-faq">
        <div className="faq-intro">
          <Reveal>
            <span className="hospitality-eyebrow">
              Frequently asked questions
            </span>

            <h2>
              Questions deserve{" "}
              <em>clear answers</em>
            </h2>

            <p>
              Talk to Techlynx about the next guest experience you want to
              improve.
            </p>
          </Reveal>

          <Reveal className="faq-image">
            <img
              src={images.service}
              alt="Hospitality service experience"
              loading="lazy"
            />

            <div className="faq-image-label">
              <span>TECHLYNX / HOSPITALITY</span>
              <strong>Human experiences. Digital precision.</strong>
            </div>
          </Reveal>
        </div>

        <div className="faq-list">
          {faqs.map(([question, answer], index) => {
            const isOpen = open === index;

            return (
              <Reveal key={question}>
                <article className={`faq-item ${isOpen ? "open" : ""}`}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? -1 : index)}
                  >
                    <span>
                      <small>0{index + 1}</small>
                      {question}
                    </span>

                    <i>
                      {isOpen ? <FiX /> : <FiPlus />}
                    </i>
                  </button>

                  <div className="faq-answer">
                    {isOpen && <p>{answer}</p>}
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CLOSING */}
      <section
        className="hospitality-closing"
        style={{ "--closing-image": `url("${images.closing}")` }}
      >
        <div className="closing-overlay" />

        <div className="closing-content">
          <Reveal>
            <span className="hospitality-eyebrow">
              Build a better hospitality future
            </span>

            <h2>
              Turn guest expectations into{" "}
              <em>digital opportunity</em>
            </h2>

            <p>
              Whether you need a booking platform, guest portal, connected
              operations, or a clear roadmap, our team can turn your ambition
              into a polished digital product.
            </p>

            <Link to="/contact" className="hospitality-primary-btn">
              Start a project
              <FiArrowUpRight />
            </Link>
          </Reveal>
        </div>

        <div className="closing-orb">
          <FiStar />
        </div>
      </section>
    </main>
  );
}